import { IUnitedSections } from 'constants/businessToolSections';
import { FOR_BANK_SECTIONS } from 'constants/businessToolSections/forBank';
import { FOR_INVESTORS_SECTIONS } from 'constants/businessToolSections/forInvestors';
import { useGetIndustryPictures } from 'services/businessPlan/hooks/useGetIndustryPictures';
import { IIndustruPictureItem, INDUSTRY_TYPE } from 'services/businessPlan/types';
import { useBusinessToolStore } from 'store/useBusinessToolStore';
import { getBusinessPlanChapters } from 'store/useBusinessToolStore/selectors';

import { transformSectionPictures } from 'pages/BusinessToolPage/PdfPreview/utils';

import { useExamplePreview } from 'hooks/useExamplePreview';

import { OTHER_SERVICES_SECTOR } from 'components/BusinessToolForm/constants/industrySectorOptions';
import { GLOBAL_FIELD_NAMES } from 'components/BusinessToolForm/Sections/Global/form';

import { AboutTheCompany } from '../AboutTheCompany/AboutTheCompany';
import { BusinessOperations } from '../BusinessOperations';
import { CoverPage } from '../CoverPage/CoverPage';
import { FinancialPlan } from '../FinancialPlan';
import { ImplementationPlan } from '../ImplementationPlan';
import { LegalFrameworkAndRisks } from '../LegalFrameworkAndRisks';
import { ManagementAndOrganization } from '../ManagementAndOrganization';
import { MarketAnalysis } from '../MarketAnalysis';
import { MarketingAndSales } from '../MarketingAndSales/MarketingAndSales';
import { ProductsAndServices } from '../ProductsAndServices';
import { RaisingAndAllocatingFunds } from '../RaisingAndAllocatingFunds';
import { Summary } from '../Summary';

interface IProps {
  saveSectionProps: (key: string, props: Record<string, any>) => void;
  propsIndustryPictures?: IIndustruPictureItem[];
}

export const useCanAccessSectionMap = ({ saveSectionProps }: { saveSectionProps: IProps['saveSectionProps'] }) => {
  const businessPlanChapters = useBusinessToolStore(getBusinessPlanChapters);
  const companySector = businessPlanChapters.GLOBAL[GLOBAL_FIELD_NAMES.COMPANY_SECTOR];
  const sector =
    typeof companySector === 'object' && companySector !== null ? companySector.value : OTHER_SERVICES_SECTOR;
  const { data: industryPictures } = useGetIndustryPictures({ type: INDUSTRY_TYPE[sector], enabled: true });
  const sectionPictures = transformSectionPictures(industryPictures);

  const { isExamplePage } = useExamplePreview();

  return new Map<IUnitedSections, React.ReactNode>([
    ['TITLE_PAGE_AND_TABLE_OF_CONTENTS', <CoverPage saveSectionProps={saveSectionProps} />],
    // For Bank
    [FOR_BANK_SECTIONS.OVERVIEW, <CoverPage saveSectionProps={saveSectionProps} />],
    [
      FOR_BANK_SECTIONS.EXECUTIVE_SUMMARY,
      <Summary saveSectionProps={saveSectionProps} sectionPicture={sectionPictures?.Section1} />,
    ],
    [
      FOR_BANK_SECTIONS.ABOUT_COMPANY,
      <AboutTheCompany saveSectionProps={saveSectionProps} sectionPicture={sectionPictures?.Section2} />,
    ],
    [
      FOR_BANK_SECTIONS.PRODUCTS_AND_SERVICES,
      <ProductsAndServices saveSectionProps={saveSectionProps} sectionPicture={sectionPictures?.Section3} />,
    ],
    [
      FOR_BANK_SECTIONS.MARKET_ANALYSIS,
      <MarketAnalysis saveSectionProps={saveSectionProps} sectionPicture={sectionPictures?.Section4} />,
    ],
    [
      FOR_BANK_SECTIONS.MARKETING_AND_SALES,
      <MarketingAndSales
        saveSectionProps={saveSectionProps}
        sectionPicture={sectionPictures?.Section5}
        isExamplePage={isExamplePage}
      />,
    ],
    [
      FOR_BANK_SECTIONS.BUSINESS_OPERATIONS,
      <BusinessOperations saveSectionProps={saveSectionProps} sectionPicture={sectionPictures?.Section6} />,
    ],
    [
      FOR_BANK_SECTIONS.MANAGEMENT_AND_ORGANIZATION,
      <ManagementAndOrganization saveSectionProps={saveSectionProps} sectionPicture={sectionPictures?.Section7} />,
    ],
    [
      FOR_BANK_SECTIONS.IMPLEMENTATION_PLAN,
      <ImplementationPlan saveSectionProps={saveSectionProps} sectionPicture={sectionPictures?.Section8} />,
    ],
    [
      FOR_BANK_SECTIONS.LEGAL_FRAMEWORKS_AND_RISKS,
      <LegalFrameworkAndRisks saveSectionProps={saveSectionProps} sectionPicture={sectionPictures?.Section9} />,
    ],
    [
      FOR_BANK_SECTIONS.RAISING_AND_ALLOCATING_FUNDS,
      <RaisingAndAllocatingFunds
        saveSectionProps={saveSectionProps}
        sectionPicture={sectionPictures?.Section10}
        isExamplePage={isExamplePage}
      />,
    ],
    [
      FOR_BANK_SECTIONS.FINANCIAL_PLAN,
      <FinancialPlan
        saveSectionProps={saveSectionProps}
        sectionPicture={sectionPictures?.Section11}
        isExamplePage={isExamplePage}
      />,
    ],
    // For Investors
    [FOR_INVESTORS_SECTIONS.OVERVIEW, <CoverPage saveSectionProps={saveSectionProps} />],
    [
      FOR_INVESTORS_SECTIONS.EXECUTIVE_SUMMARY,
      <Summary saveSectionProps={saveSectionProps} sectionPicture={sectionPictures?.Section1} />,
    ],
    [
      FOR_INVESTORS_SECTIONS.COMPANY_AND_PROJECT,
      <AboutTheCompany saveSectionProps={saveSectionProps} sectionPicture={sectionPictures?.Section2} />,
    ],
    [
      FOR_INVESTORS_SECTIONS.MARKET_ANALYSIS,
      <MarketAnalysis saveSectionProps={saveSectionProps} sectionPicture={sectionPictures?.Section4} />,
    ],
    [
      FOR_INVESTORS_SECTIONS.MARKETING_AND_SALES,
      <MarketingAndSales
        saveSectionProps={saveSectionProps}
        sectionPicture={sectionPictures?.Section5}
        isExamplePage={isExamplePage}
      />,
    ],
    [
      FOR_INVESTORS_SECTIONS.BUSINESS_OPERATIONS,
      <BusinessOperations saveSectionProps={saveSectionProps} sectionPicture={sectionPictures?.Section6} />,
    ],
    [
      FOR_INVESTORS_SECTIONS.MANAGEMENT_AND_ORGANIZATION,
      <ManagementAndOrganization saveSectionProps={saveSectionProps} sectionPicture={sectionPictures?.Section7} />,
    ],
    [
      FOR_INVESTORS_SECTIONS.RAISING_AND_ALLOCATING_FUNDS,
      <RaisingAndAllocatingFunds
        saveSectionProps={saveSectionProps}
        sectionPicture={sectionPictures?.Section10}
        isExamplePage={isExamplePage}
      />,
    ],
    [
      FOR_INVESTORS_SECTIONS.FINANCIAL_PLAN,
      <FinancialPlan
        saveSectionProps={saveSectionProps}
        sectionPicture={sectionPictures?.Section11}
        isExamplePage={isExamplePage}
      />,
    ],
  ]);
};
